// ── Canonical Project Readiness Model ──
// Single source of truth for deriving readiness state from Job fields.
// Consumed by: portal intelligence, internal dashboards, automation logic.
// This file is ADDITIVE — it does not modify any existing types or logic.

import type {
  Job,
  Issue,
  PermitStatus,
  MaterialReadiness as MaterialReadinessType,
  DispatchStatus,
  PaymentStatus,
  JobStage,
} from "./types";
import { deriveBuildReviewStatus, type BuildReviewEvent } from "./buildReviewTypes";
import { isKitGateCleared, type MaterialKit } from "./materialKit";
import { assertStageProofIntegrity } from "./stageProofIntegrity";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

// ── Canonical Status Enum ──
// Every readiness category resolves to exactly one of these four states.
export type CanonicalReadinessStatus = "ready" | "pending" | "at_risk" | "blocked";

// ── Per-Category Canonical Types ──

export type PermitReadinessState =
  | "not_required"
  | "not_submitted"
  | "submitted"
  | "approved"
  | "revision_needed";

export type MaterialReadinessState =
  | "not_ordered"
  | "ordered"
  | "partially_received"
  | "fully_received"
  | "staged"
  | "loaded"
  | "missing_items"
  | "field_purchase_required";

export type CrewReadinessState =
  | "unassigned"
  | "dispatched_pending"
  | "rejected_reassigning"
  | "assigned";

export type ScheduleReadinessState =
  | "not_scheduled"
  | "tentative"
  | "confirmed"
  | "past_due";

export type SiteReadinessState =
  | "not_verified"
  | "partially_verified"
  | "verified";

export type FinancialReadinessState =
  | "not_invoiced"
  | "invoiced"
  | "partial_payment"
  | "paid"
  | "overdue"
  | "deposit_requested"
  | "deposit_received"
  | "final_payment_pending"
  | "paid_in_full";

export type BuildReviewReadinessState =
  | "not_started"
  | "pending"
  | "approved"
  | "rejected"
  | "not_evaluated"; // sentinel — only emitted by deriveReadinessFromJobExcludingBuildReview

export type JobSpecReadinessState =
  | "not_bound"
  | "bound";

// ── Readiness Item ──

export interface CanonicalReadinessItem<S extends string = string> {
  category: string;
  state: S;
  status: CanonicalReadinessStatus;
  label: string;
  shortText: string;
  description?: string;
}

export interface DerivedReadiness {
  jobSpec: CanonicalReadinessItem<JobSpecReadinessState>;
  permit: CanonicalReadinessItem<PermitReadinessState>;
  materials: CanonicalReadinessItem<MaterialReadinessState>;
  crew: CanonicalReadinessItem<CrewReadinessState>;
  schedule: CanonicalReadinessItem<ScheduleReadinessState>;
  site: CanonicalReadinessItem<SiteReadinessState>;
  financial: CanonicalReadinessItem<FinancialReadinessState>;
  buildReview: CanonicalReadinessItem<BuildReviewReadinessState>;
  overall: CanonicalReadinessStatus;
  readyCount: number;
  totalCount: number;
}

// ── Mapping Tables ──

const PERMIT_MAP: Record<PermitStatus, { state: PermitReadinessState; status: CanonicalReadinessStatus }> = {
  "Not Required": { state: "not_required", status: "ready" },
  "Approved":     { state: "approved", status: "ready" },
  "Submitted":    { state: "submitted", status: "pending" },
  "Revision Needed": { state: "revision_needed", status: "blocked" },
};

const MATERIAL_MAP: Record<MaterialReadinessType, { state: MaterialReadinessState; status: CanonicalReadinessStatus }> = {
  "Not Ordered":           { state: "not_ordered", status: "pending" },
  "Ordered":               { state: "ordered", status: "pending" },
  "Partially Received":    { state: "partially_received", status: "at_risk" },
  "Fully Received":        { state: "fully_received", status: "ready" },
  "Staged for Job":        { state: "staged", status: "ready" },
  "Loaded for Crew":       { state: "loaded", status: "ready" },
  "Missing Items":         { state: "missing_items", status: "blocked" },
  "Field Purchase Required": { state: "field_purchase_required", status: "blocked" },
};

const PAYMENT_MAP: Record<PaymentStatus, { state: FinancialReadinessState; status: CanonicalReadinessStatus }> = {
  "Not Invoiced":          { state: "not_invoiced", status: "ready" },
  "Invoiced":              { state: "invoiced", status: "at_risk" },
  "Partial Payment":       { state: "partial_payment", status: "pending" },
  "Paid":                  { state: "paid", status: "ready" },
  "Overdue":               { state: "overdue", status: "blocked" },
  "Deposit Requested":     { state: "deposit_requested", status: "pending" },
  "Deposit Received":      { state: "deposit_received", status: "ready" },
  "Final Payment Pending": { state: "final_payment_pending", status: "pending" },
  "Paid in Full":          { state: "paid_in_full", status: "ready" },
};

// ── Derivation Helpers ──

/**
 * JobSpec Bound — execution lock prerequisite.
 *
 * "Valid pinned spec" = job.boundJobSpec exists AND boundJobSpec.snapshot
 * exists. Legacy bindings without a snapshot (see jobSpec.isLegacyBinding)
 * are treated as NOT bound for readiness purposes — Phase 1 lock requires a
 * fully pinned spec, no fallback to the live registry.
 */
function deriveJobSpec(job: Job): CanonicalReadinessItem<JobSpecReadinessState> {
  const bound = job.boundJobSpec;
  if (bound && bound.snapshot) {
    return {
      category: "jobSpec",
      state: "bound",
      status: "ready",
      label: "JobSpec",
      shortText: `${bound.specId}-v${bound.versionNumber}`,
    };
  }
  return {
    category: "jobSpec",
    state: "not_bound",
    status: "blocked",
    label: "JobSpec",
    shortText: "Not bound",
    description: "Job has no pinned JobSpec snapshot. Bind a spec before dispatch.",
  };
}

function derivePermit(job: Job): CanonicalReadinessItem<PermitReadinessState> {
  if (migrationBypasses(job, "permitting")) {
    return {
      category: "permits",
      state: "approved",
      status: "ready",
      label: "Permits",
      shortText: "Bypassed",
      description: "Permitting was bypassed during migration review.",
    };
  }

  const raw = job.permitStatus;
  const mapped = PERMIT_MAP[raw] ?? { state: "not_submitted" as const, status: "pending" as const };
  return {
    category: "permits",
    state: mapped.state,
    status: !job.permitRequired ? "ready" : mapped.status,
    label: "Permits",
    shortText: !job.permitRequired ? "Not required" : raw,
    description: mapped.state === "revision_needed"
      ? "Permit requires revisions before approval."
      : mapped.state === "submitted"
        ? "Permit submitted — awaiting authority approval."
        : undefined,
  };
}

/**
 * Materials readiness — material lifecycle AND material-kit gate.
 *
 * INTENTIONAL GLOBAL BEHAVIOR CHANGE (Phase 1 Dispatch Lock):
 * The Materials factor passes ("ready") only if the underlying material
 * lifecycle is satisfied AND `isKitGateCleared(kit)` is true. The kit gate
 * is folded in here rather than added as a separate readiness row, so every
 * consumer of `derived.materials` (panels, scheduling, install readiness,
 * roll-ups via `derived.overall`) reflects kit clearance automatically.
 *
 * If `kit` is omitted, the kit fold is skipped (legacy callers retain
 * pre-Phase-1 behavior). Always pass `kit` from new call sites.
 *
 * ALIGNMENT FOLD (description-only):
 * If `alignmentSummary` is provided AND the materials are not aligned, the
 * existing description string is appended with a plain-language note. This
 * does NOT change the readiness status — the canonical Material Alignment
 * Gate is the enforcement layer at dispatch/install. Here we only surface
 * WHY a downstream gate may block, so panels reading `derived.materials`
 * tell the same story the dispatcher and installer already see.
 */
function deriveMaterials(
  job: Job,
  kit?: MaterialKit,
  alignmentSummary?: { aligned: boolean; note?: string },
): CanonicalReadinessItem<MaterialReadinessState> {
  if (migrationBypasses(job, "ordering")) {
    return {
      category: "materials",
      state: "ordered",
      status: "ready",
      label: "Materials",
      shortText: "Ordering bypassed",
      description: "Ordering/material readiness was bypassed during migration review. Warehouse can still allocate materials if needed.",
    };
  }

  const raw = (() => {
    const v = String(job.materialReadiness ?? "").trim();
    if (v === "Received") return "Fully Received";
    if (v === "Staged") return "Staged for Job";
    if (v === "Loaded") return "Loaded for Crew";
    return v;
  })();
  const mapped = MATERIAL_MAP[raw] ?? { state: "not_ordered" as const, status: "pending" as const };

  // ── Stage-inference REMOVED (Stage Proof Integrity Guard) ──
  // Previously, a job whose materialReadiness was "Not Ordered" but whose
  // currentStage was past Ordered would be silently upgraded to "ordered".
  // That masked contradictions and let unfinished jobs reach Install. The
  // guard in stageProofIntegrity.ts now treats this as a violation.
  const base: CanonicalReadinessItem<MaterialReadinessState> = {
    category: "materials",
    state: mapped.state,
    status: mapped.status,
    label: "Materials",
    shortText: raw,
    description: mapped.status === "blocked"
      ? "Required materials are missing or need field purchase."
      : mapped.status === "at_risk"
        ? "Some materials received; remaining items in transit."
        : undefined,
  };

  // ── Kit fold ──
  // Only when caller provided kit context. Kit-not-cleared downgrades a
  // would-be "ready" material state to "blocked" — the truck cannot roll
  // without confirmed kit (or a logged PM override).
  let folded = base;
  if (kit !== undefined && base.status === "ready" && !isKitGateCleared(kit)) {
    folded = {
      ...base,
      status: "blocked",
      shortText: `${base.shortText} · Kit not cleared`,
      description: "Material lifecycle complete but Material Kit is not confirmed (or no PM override). Confirm all kit items before dispatch.",
    };
  }
  // Kit missing entirely (no kit yet generated for a bound spec) is also a block.
  else if (kit === undefined && job.boundJobSpec?.snapshot && base.status === "ready") {
    folded = {
      ...base,
      status: "blocked",
      shortText: `${base.shortText} · Kit not generated`,
      description: "Material Kit has not been generated from the bound JobSpec. Initialize the kit before dispatch.",
    };
  }

  // ── Alignment description fold (no status change) ──
  if (alignmentSummary && !alignmentSummary.aligned) {
    const note = alignmentSummary.note?.trim() ||
      "Materials out of sync: spec, PO, and kit do not match.";
    return {
      ...folded,
      description: folded.description ? `${folded.description} ${note}` : note,
    };
  }
  return folded;
}

function deriveCrew(job: Job): CanonicalReadinessItem<CrewReadinessState> {
  if (job.assignedSubcontractor || job.assignedCrew) {
    return { category: "crew", state: "assigned", status: "ready", label: "Crew Assignment", shortText: job.assignedSubcontractor || job.assignedCrew || "Assigned" };
  }
  if (job.dispatchStatus === "Rejected") {
    return { category: "crew", state: "rejected_reassigning", status: "at_risk", label: "Crew Assignment", shortText: "Reassigning", description: "Previous assignment was rejected; reassignment in progress." };
  }
  if (job.dispatchStatus === "Sent to Contractor Network" || job.dispatchStatus === "Awaiting Response") {
    return { category: "crew", state: "dispatched_pending", status: "pending", label: "Crew Assignment", shortText: "Pending response" };
  }
  return { category: "crew", state: "unassigned", status: "pending", label: "Crew Assignment", shortText: "Not yet assigned" };
}

function deriveSchedule(job: Job): CanonicalReadinessItem<ScheduleReadinessState> {
  // ── Stage-inference safety net (legacy seed/data only) ──
  // Some post-scheduling stages (notably "Available for Contractor Assignment")
  // imply scheduling has occurred at the workflow level even when no concrete
  // scheduledInstallDate has been set on the job. Conservatively report
  // "tentative/pending" (NOT "confirmed/ready") so the contradiction with
  // stage progression is removed without overstating certainty. New code
  // paths should set scheduledInstallDate explicitly.
  const POST_SCHEDULE_STAGES: JobStage[] = [
    "Scheduled Tentative",
    "Scheduled Confirmed",
    "Ready for Dispatch",
    "Available for Contractor Assignment",
    "Assigned and Dispatched",
    "In Progress",
    "QA Review",
    "Completed",
  ];

  if (!job.scheduledInstallDate) {
    if (POST_SCHEDULE_STAGES.includes(job.currentStage)) {
      return {
        category: "schedule",
        state: "tentative",
        status: "pending",
        label: "Schedule",
        shortText: "Tentative",
        description: "Scheduled at the workflow level (inferred from stage); concrete date pending.",
      };
    }
    return { category: "schedule", state: "not_scheduled", status: "pending", label: "Schedule", shortText: "Date pending" };
  }
  const d = new Date(job.scheduledInstallDate);
  const isPast = d < new Date() && job.currentStage !== "Completed" && job.currentStage !== "In Progress";
  if (isPast) {
    return { category: "schedule", state: "past_due", status: "at_risk", label: "Schedule", shortText: "Past due — needs rescheduling" };
  }
  const isConfirmed = job.currentStage === "Scheduled Confirmed" || job.currentStage === "Ready for Dispatch" || job.currentStage === "Assigned and Dispatched";
  return {
    category: "schedule",
    state: isConfirmed ? "confirmed" : "tentative",
    status: isConfirmed ? "ready" : "pending",
    label: "Schedule",
    shortText: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
  };
}

function deriveSite(job: Job): CanonicalReadinessItem<SiteReadinessState> {
  const sr = job.siteReadiness;

  // If structured site readiness data exists, use it
  if (sr) {
    const checks = [sr.accessConfirmed, sr.utilitiesReady, sr.customerPrepared];
    const passedCount = checks.filter(Boolean).length;

    if (passedCount === 3) {
      return { category: "site", state: "verified", status: "ready", label: "Site Readiness", shortText: "Confirmed" };
    }
    if (passedCount > 0) {
      const missing: string[] = [];
      if (!sr.accessConfirmed) missing.push("access");
      if (!sr.utilitiesReady) missing.push("utilities");
      if (!sr.customerPrepared) missing.push("customer prep");
      return {
        category: "site",
        state: "partially_verified",
        status: "at_risk",
        label: "Site Readiness",
        shortText: `Pending: ${missing.join(", ")}`,
        description: `Site is partially verified. Outstanding: ${missing.join(", ")}.`,
      };
    }
    return { category: "site", state: "not_verified", status: "pending", label: "Site Readiness", shortText: "Verification pending", description: "Site conditions have not been confirmed." };
  }

  // Fallback: infer from stage progression (backward compatible)
  const advancedStages: string[] = ["Scheduled Confirmed", "Ready for Dispatch", "Assigned and Dispatched", "In Progress", "QA Review", "Completed"];
  const verified = advancedStages.includes(job.currentStage);
  return {
    category: "site",
    state: verified ? "verified" : "not_verified",
    status: verified ? "ready" : "pending",
    label: "Site Readiness",
    shortText: verified ? "Confirmed" : "Verification pending",
    description: !verified ? "Site conditions will be confirmed before installation." : undefined,
  };
}

function deriveFinancial(job: Job): CanonicalReadinessItem<FinancialReadinessState> {
  const ps = job.financials?.paymentStatus;
  if (!ps) return { category: "financial", state: "not_invoiced", status: "ready", label: "Financial Status", shortText: "On track" };
  const mapped = PAYMENT_MAP[ps] ?? { state: "not_invoiced" as const, status: "ready" as const };
  return {
    category: "financial",
    state: mapped.state,
    status: mapped.status,
    label: "Financial Status",
    shortText: ps,
    description: mapped.status === "blocked" ? "Payment is overdue."
      : mapped.status === "at_risk" ? "Invoice outstanding."
      : mapped.state === "deposit_requested" ? "Deposit has been requested from the customer."
      : mapped.state === "final_payment_pending" ? "Final payment is pending before project closeout."
      : undefined,
  };
}

/**
 * Derive build review status using the events log as the canonical primary
 * source. Fallback chain (in order):
 *
 *   1. BuildReviewEvent[] for this job  → events are the truth model
 *   2. Legacy job-level boolean / status flags (compatibility)
 *   3. Stage-based inference (legacy/seed safety net only)
 *   4. "not_started" / pending
 *
 * Stage inference is intentionally LAST — it exists to keep historical seed
 * data from showing as "Pending Build Review" while simultaneously being
 * Completed. New code paths must emit BuildReviewEvents, not rely on this.
 */
function deriveBuildReview(
  job: Job,
  buildReviewEvents: BuildReviewEvent[],
): CanonicalReadinessItem<BuildReviewReadinessState> {
  if (migrationBypasses(job, "build_review")) {
    return {
      category: "buildReview",
      state: "approved",
      status: "ready",
      label: "Build Review",
      shortText: "Bypassed",
      description: "Build Review was bypassed during migration review.",
    };
  }

  // ── 1. Events are the canonical truth ──
  const jobEvents = buildReviewEvents.filter((e) => e.jobId === job.id);
  if (jobEvents.length > 0) {
    const eventStatus = deriveBuildReviewStatus(jobEvents);
    if (eventStatus === "Approved") {
      return { category: "buildReview", state: "approved", status: "ready", label: "Build Review", shortText: "Approved" };
    }
    if (eventStatus === "Revision Required" || eventStatus === "On Hold — Sales Action Required") {
      return {
        category: "buildReview",
        state: "rejected",
        status: "blocked",
        label: "Build Review",
        shortText: eventStatus === "Revision Required" ? "Revision required" : "On hold",
        description: "Build review rejected — revision required before progression.",
      };
    }
    // Any other event-derived status (Awaiting Sales, Awaiting Customer Sig,
    // Final Review, Resubmitted, Needs Review) maps to "pending" / under review.
    return {
      category: "buildReview",
      state: "pending",
      status: "pending",
      label: "Build Review",
      shortText: eventStatus,
    };
  }

  // ── 2. Legacy boolean / status flag fallback ──
  if (job.buildReviewStatus) {
    const statusMap: Record<string, { state: BuildReviewReadinessState; status: CanonicalReadinessStatus; shortText: string }> = {
      not_started: { state: "not_started", status: "pending", shortText: "Not started" },
      pending:     { state: "pending", status: "pending", shortText: "Under review" },
      approved:    { state: "approved", status: "ready", shortText: "Approved" },
      rejected:    { state: "rejected", status: "blocked", shortText: "Rejected" },
    };
    const m = statusMap[job.buildReviewStatus] ?? statusMap.not_started;
    return {
      category: "buildReview",
      state: m.state,
      status: m.status,
      label: "Build Review",
      shortText: m.shortText,
      description: m.state === "rejected" ? "Build review was rejected — revision required." : undefined,
    };
  }
  if (job.buildReviewApproved === true || job.intakeApproved === true) {
    return { category: "buildReview", state: "approved", status: "ready", label: "Build Review", shortText: "Approved" };
  }
  if (job.approvedForOrdering === true) {
    return { category: "buildReview", state: "approved", status: "ready", label: "Build Review", shortText: "Approved" };
  }
  if (job.holdStatus) {
    return { category: "buildReview", state: "rejected", status: "blocked", label: "Build Review", shortText: "On hold", description: job.holdReason || "Build review placed on hold." };
  }

  // ── Stage-based inference REMOVED (Stage Proof Integrity Guard) ──
  // Previously, any job whose currentStage was past Build Review was
  // silently treated as "Approved" — masking missing proof. The integrity
  // guard (stageProofIntegrity.ts) now raises this contradiction explicitly.
  return { category: "buildReview", state: "not_started", status: "pending", label: "Build Review", shortText: "Pending" };
}

// ── Main Derivation Function ──
// This is the SINGLE SOURCE of readiness truth. Build review state is read
// from BuildReviewEvent[] as the canonical primary source. Events are required
// — there is no silent optional path here.
//
// Pure utilities that do not consume the build review branch should call
// deriveReadinessFromJobExcludingBuildReview(job) instead (see below).

export function deriveReadinessFromJob(
  job: Job,
  buildReviewEvents: BuildReviewEvent[],
  kit?: MaterialKit,
): DerivedReadiness {
  const jobSpec = deriveJobSpec(job);
  const permit = derivePermit(job);
  const materials = deriveMaterials(job, kit);
  const crew = deriveCrew(job);
  const schedule = deriveSchedule(job);
  const site = deriveSite(job);
  const financial = deriveFinancial(job);
  const buildReview = deriveBuildReview(job, buildReviewEvents);

  const all = [jobSpec, permit, materials, crew, schedule, site, financial, buildReview];
  const readyCount = all.filter(i => i.status === "ready").length;
  const hasBlocked = all.some(i => i.status === "blocked");
  const hasAtRisk = all.some(i => i.status === "at_risk");
  const hasPending = all.some(i => i.status === "pending");

  let overall: CanonicalReadinessStatus = "ready";
  if (hasBlocked) overall = "blocked";
  else if (hasAtRisk) overall = "at_risk";
  else if (hasPending) overall = "pending";

  return { jobSpec, permit, materials, crew, schedule, site, financial, buildReview, overall, readyCount, totalCount: 8 };
}

/**
 * Compatibility helper for pure utilities that DO NOT consume the build
 * review branch (e.g. financialClearance, invoiceEngine — they only inspect
 * `derived.financial`). Returns the canonical shape with `buildReview` set
 * to a sentinel `not_evaluated` status.
 *
 * ⚠️ NOT THE CANONICAL PATH.
 *
 * Do NOT use this from:
 *   • UI surfaces that render readiness state
 *   • Workflow gating, scheduling, or dispatch decisions
 *   • Anything where build review status affects UX or business outcomes
 *
 * If a consumer reads `derived.overall`, `derived.readyCount`, or any
 * cross-category roll-up, it MUST go through `deriveReadinessFromJob` with
 * real events — because build review participates in those roll-ups.
 */
export function deriveReadinessFromJobExcludingBuildReview(job: Job): DerivedReadiness {
  const jobSpec = deriveJobSpec(job);
  const permit = derivePermit(job);
  const materials = deriveMaterials(job);
  const crew = deriveCrew(job);
  const schedule = deriveSchedule(job);
  const site = deriveSite(job);
  const financial = deriveFinancial(job);
  const buildReview: CanonicalReadinessItem<BuildReviewReadinessState> = {
    category: "buildReview",
    state: "not_evaluated",
    status: "ready", // neutral — does not contaminate roll-ups for callers that
                     // explicitly opted out of evaluating this branch.
    label: "Build Review",
    shortText: "Not evaluated",
  };

  // Roll-ups intentionally exclude buildReview from the not-ready signals.
  // jobSpec IS included — it's a structural readiness concern, not a review one.
  const considered = [jobSpec, permit, materials, crew, schedule, site, financial];
  const readyCount = considered.filter(i => i.status === "ready").length;
  const hasBlocked = considered.some(i => i.status === "blocked");
  const hasAtRisk = considered.some(i => i.status === "at_risk");
  const hasPending = considered.some(i => i.status === "pending");

  let overall: CanonicalReadinessStatus = "ready";
  if (hasBlocked) overall = "blocked";
  else if (hasAtRisk) overall = "at_risk";
  else if (hasPending) overall = "pending";

  return { jobSpec, permit, materials, crew, schedule, site, financial, buildReview, overall, readyCount, totalCount: 7 };
}

// ── Bridge Functions ──
// These provide backward-compatible interfaces so existing consumers
// (JobWorkflowStatus, dashboards, scheduling) can delegate to canonical engine
// without changing their call signatures.

export type InstallReadinessStatus = "Ready for Install" | "Partially Ready" | "Not Ready";

function mapOverallToInstallReadiness(derived: DerivedReadiness): InstallReadinessStatus {
  if (derived.overall === "ready") return "Ready for Install";
  if (derived.overall === "blocked") return "Not Ready";
  // at_risk or pending
  if (derived.readyCount >= 6) return "Partially Ready"; // 6+ of 8 pass
  return "Not Ready";
}

export function getCanonicalInstallReadiness(
  job: Job,
  _openIssues: Issue[] | undefined,
  buildReviewEvents: BuildReviewEvent[],
  kit?: MaterialKit,
): {
  status: InstallReadinessStatus;
  score: number;
  checks: { label: string; ok: boolean; critical: boolean }[];
  blockerCount: number;
  criticalCount: number;
  integrityViolations?: { gate: string; reason: string }[];
} {
  const derived = deriveReadinessFromJob(job, buildReviewEvents, kit);
  const all = [derived.jobSpec, derived.permit, derived.materials, derived.crew, derived.schedule, derived.site, derived.financial, derived.buildReview];

  const checks = all.map(item => ({
    label: item.label,
    ok: item.status === "ready",
    critical: item.status === "blocked",
  }));

  // ── Stage Proof Integrity Guard ──
  // Even if every readiness factor is "ready", a stage/proof contradiction
  // (e.g. job stage past Build Review with no approval proof) hard-blocks
  // install. This prevents seeded/contradictory jobs from appearing
  // installable. Surfaced as a synthetic "Stage Integrity" check.
  const integrity = assertStageProofIntegrity(job, buildReviewEvents);
  if (!integrity.ok) {
    checks.push({ label: "Stage Integrity", ok: false, critical: true });
  }

  const blockerCount = checks.filter(c => !c.ok).length;
  const criticalCount = checks.filter(c => c.critical).length;
  const score = Math.round((derived.readyCount / derived.totalCount) * 100);

  const status: InstallReadinessStatus = !integrity.ok
    ? "Not Ready"
    : mapOverallToInstallReadiness(derived);

  return {
    status,
    score,
    checks,
    blockerCount,
    criticalCount,
    integrityViolations: integrity.ok
      ? undefined
      : integrity.violations.map(v => ({ gate: v.gate, reason: v.reason })),
  };
}

export function getCanonicalReadinessScore(
  job: Job,
  buildReviewEvents: BuildReviewEvent[],
  kit?: MaterialKit,
): number {
  const derived = deriveReadinessFromJob(job, buildReviewEvents, kit);
  return Math.round((derived.readyCount / derived.totalCount) * 100);
}

// ── Explainability ──
// Returns structured explanation of what's blocking readiness and why.

export interface ReadinessExplanation {
  overallStatus: CanonicalReadinessStatus;
  score: number;
  blockers: { category: string; label: string; state: string; shortText: string; description?: string }[];
  atRisk: { category: string; label: string; state: string; shortText: string; description?: string }[];
  summary: string;
}

export function getReadinessExplanation(
  job: Job,
  buildReviewEvents: BuildReviewEvent[],
  kit?: MaterialKit,
): ReadinessExplanation {
  const derived = deriveReadinessFromJob(job, buildReviewEvents, kit);
  const all = [derived.jobSpec, derived.permit, derived.materials, derived.crew, derived.schedule, derived.site, derived.financial, derived.buildReview];

  const blockers = all.filter(i => i.status === "blocked").map(i => ({
    category: i.category, label: i.label, state: i.state, shortText: i.shortText, description: i.description,
  }));
  const atRisk = all.filter(i => i.status === "at_risk").map(i => ({
    category: i.category, label: i.label, state: i.state, shortText: i.shortText, description: i.description,
  }));

  const score = Math.round((derived.readyCount / derived.totalCount) * 100);

  let summary: string;
  if (derived.overall === "ready") {
    summary = "All readiness requirements are met.";
  } else if (blockers.length > 0) {
    summary = `Blocked by: ${blockers.map(b => b.label).join(", ")}. ${atRisk.length > 0 ? `At risk: ${atRisk.map(r => r.label).join(", ")}.` : ""}`;
  } else if (atRisk.length > 0) {
    summary = `At risk: ${atRisk.map(r => r.label).join(", ")}.`;
  } else {
    summary = "Some requirements are pending.";
  }

  return { overallStatus: derived.overall, score, blockers, atRisk, summary };
}

// ── Gap Analysis (documented inline) ──
//
// CLOSED GAPS (this pass):
//
// 1. Site Readiness — NOW uses structured SiteReadinessInfo from Job
//    when available, with stage-inference fallback for backward compatibility.
//
// 2. Deposit / Partial Payment granularity —
//    PaymentStatus now includes "Deposit Requested", "Deposit Received",
//    "Final Payment Pending", and "Paid in Full".
//
// 3. Build Review — NOW a 7th readiness category with four states:
//    not_started, pending, approved, rejected.
//    Mapped from buildReviewStatus, buildReviewApproved, intakeApproved,
//    approvedForOrdering, and holdStatus fields.
//
// REMAINING GAPS:
//
// 4. Permit "Rejected" state —
//    PermitStatus uses "Revision Needed" not "Rejected".
//    Portal intelligence checks for "Revision Needed" (correct).
//
// 5. QA Readiness —
//    JobWorkflowStatus tracks qaSubmitted but the canonical model
//    does not include it (it's post-install, not pre-install readiness).
//
// 6. Open Issues —
//    JobWorkflowStatus factors in open issues count/severity.
//    The canonical model focuses on structural readiness fields.
//    Issues can be layered on top via separate concern.
//
// FIELD MAPPING SUMMARY:
//
// | Category     | Source Field(s)                          | Canonical State Type          |
// |--------------|------------------------------------------|-------------------------------|
// | Permits      | permitStatus, permitRequired             | PermitReadinessState          |
// | Materials    | materialReadiness                        | MaterialReadinessState        |
// | Crew         | assignedSubcontractor,                   | CrewReadinessState            |
// |              | assignedCrew, dispatchStatus              |                               |
// | Schedule     | scheduledInstallDate, currentStage       | ScheduleReadinessState        |
// | Site         | siteReadiness (structured) or            | SiteReadinessState            |
// |              | currentStage (inferred fallback)         |                               |
// | Financial    | financials.paymentStatus                 | FinancialReadinessState       |
// | Build Review | buildReviewStatus, buildReviewApproved,  | BuildReviewReadinessState     |
// |              | intakeApproved, approvedForOrdering,      |                               |
// |              | holdStatus                               |                               |
